import type { ReactElement } from "react"
import type { AdvancedProductCardMolProps } from "@onestore/hel/dist/components/molecules/AdvancedProductCardMol"
import AdvancedProductCardMol from "@onestore/hel/dist/components/molecules/AdvancedProductCardMol"
import { PriceVariantType } from "@onestore/hel/dist/components/molecules/PriceMol/priceMol.types"
import _t from "@core/i18n"
import DetailedPrice from "@gatsby-plugin-generic-page/components/DetailedPrice"
import type { CtaAXLink } from "@gatsby-plugin-generic-page/fragments/ctaAXLink"
import type { InternalLink } from "@gatsby-plugin-generic-page/fragments/internalLink"
import type { ProductCardFlatData } from "@gatsby-plugin-generic-page/fragments/productCard"
import getLinkProps from "@gatsby-plugin-generic-page/helpers/getLinkProps"
import getProductCardLogo from "@gatsby-plugin-generic-page/helpers/getLogo"
import getProductCardFooterData from "@gatsby-plugin-generic-page/helpers/getProductCardFooterData"
import getProductCardPeriodText from "@gatsby-plugin-generic-page/helpers/getProductCardPeriodText"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import { typeMatches } from "@onestore-graphql"
import Price from "~/components/AdvancedProductCard/components/Price"
import StaticPrice from "~/components/AdvancedProductCard/components/StaticPrice"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import usePriceConfigurationQuery from "~/hooks/usePriceConfigurationQuery"
import { sendGaEvent } from "~/lib/analytics"
import assetsSquidexToImageObject from "~/lib/assetsSquidexToImageObject"
import isEmpty from "~/lib/isEmpty"
import url from "~/lib/url"
import { showPlanOverLimitModal } from "~/store/basket/actions"
import { getPlansUsageOverLimit } from "~/store/basket/selectors"
import { useAppDispatch, useAppSelector } from "~/store/hooks"
import type { ExternalLink } from "~/types/squidex"

function ProductCard(
  card: ProductCardFlatData
): ReactElement<AdvancedProductCardMolProps> {
  const dispatch = useAppDispatch()
  const { currentPriceType } = usePriceTypeContext()
  const { showDetailedPrices } = usePriceConfigurationQuery()
  const plansOverLimit = useAppSelector(getPlansUsageOverLimit)

  const {
    headerImage,
    heading,
    text,
    button,
    cardLink,
    label,
    logo,
    logoBackgroundColor,
    footer,
  } = card.flatData

  const link = getLinkProps(cardLink, currentPriceType)
  const {
    productPeriodInfo,
    staticPrice,
    priceHasPrimaryColor,
    priceText,
    tooltip,
    priceInfo,
    planId,
  } = getProductCardFooterData(footer, currentPriceType)
  const { logoElement, textBox } = getProductCardLogo(logo, logoBackgroundColor)
  let isPlanOverLimit = false

  if (planId) {
    isPlanOverLimit = plansOverLimit.includes(planId)
  }

  const {
    color: buttonColor,
    gaEvent,
    text: buttonText,
    title,
    variant,
    link: buttonLink,
  } = button

  let href: string | undefined,
    to: string | undefined,
    openInNewTab: boolean | undefined

  if (typeMatches(buttonLink.__typename, SquidexComponentType.EXTERNAL_LINK)) {
    const { href: hrefLink, openInNewTab: openNewTab } =
      buttonLink as ExternalLink

    href = hrefLink
    openInNewTab = openNewTab
  }

  if (typeMatches(buttonLink.__typename, SquidexComponentType.INTERNAL_LINK)) {
    const { pages, openInNewTab: openNewTab } = buttonLink as InternalLink

    to = !isEmpty(pages) ? pages[0].flatData.url : undefined
    openInNewTab = openNewTab
  }

  if (typeMatches(buttonLink.__typename, SquidexComponentType.CTA_AX_LINK)) {
    href = url.generateBonusUrlByCtaAxLink(
      buttonLink as CtaAXLink,
      currentPriceType
    )
  }

  const onCardButtonClick = () => {
    if (isPlanOverLimit) {
      dispatch(showPlanOverLimitModal())
    } else {
      sendGaEvent(gaEvent)
    }
  }

  const productTileLabel = !isEmpty(label)
    ? { children: label.text, variant: label.variant }
    : undefined

  const periodText = getProductCardPeriodText(
    productPeriodInfo,
    currentPriceType
  )

  const hasPromotion = productPeriodInfo?.hasPromotion() ?? false

  const detailedPriceJSX =
    showDetailedPrices && !isEmpty(productPeriodInfo) ? (
      <div>
        <DetailedPrice
          variant={{ type: PriceVariantType.SMALL, align: "left" }}
          periodInfo={productPeriodInfo}
          priceInfo={priceInfo}
        />
      </div>
    ) : null

  const productPrice = !isEmpty(productPeriodInfo) ? (
    <Price
      periodInfo={productPeriodInfo}
      priceType={currentPriceType}
      periodText={periodText}
      priceHasPrimaryColor={priceHasPrimaryColor}
      tooltip={tooltip}
    />
  ) : null

  return (
    <>
      <AdvancedProductCardMol
        backgroundImage={assetsSquidexToImageObject(headerImage, "")}
        title={heading}
        description={text}
        link={link}
        logo={logoElement}
        textBox={textBox}
        label={
          hasPromotion
            ? {
                children: _t("marketplace.discountTitle"),
                variant: "primaryOutlined",
              }
            : productTileLabel
        }
        price={
          productPeriodInfo ? (
            detailedPriceJSX || productPrice
          ) : staticPrice ? (
            <StaticPrice
              {...staticPrice}
              priceHasPrimaryColor={priceHasPrimaryColor}
            />
          ) : undefined
        }
        priceText={priceText}
        button={{
          text: buttonText,
          title: title,
          openInNewTab: openInNewTab,
          href: isPlanOverLimit ? undefined : href,
          to: isPlanOverLimit ? undefined : to,
          onClick: onCardButtonClick,
        }}
        buttonColor={!isEmpty(buttonColor) ? buttonColor.color : undefined}
        buttonVariant={!isEmpty(variant) ? variant.variant : undefined}
      />
    </>
  )
}

export default ProductCard
